import LegalLayout from '@/components/feature/LegalLayout';
import { useTranslation } from 'react-i18next';

export default function CookiePolicy() {
  const { t } = useTranslation();
  return (
    <LegalLayout title={t('cookies.title', 'Cookie Policy')} lastUpdated="May 20, 2026">
      <div className="space-y-8">
        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s1', '1. What Are Cookies')}</h2>
          <p className="leading-relaxed">
            Cookies are small text files that are stored on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently, as well as to provide information to the owners of the site. Cookies enable us to recognize your device and remember your preferences and actions over time.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s2', '2. How We Use Cookies')}</h2>
          <p className="leading-relaxed mb-3">
            ZhiSu uses cookies and similar tracking technologies for the following purposes:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Essential Cookies:</strong> Necessary for the website to function properly. These enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies.</li>
            <li><strong>Preference Cookies:</strong> Allow the website to remember choices you make (such as your language preference, theme selection, or region) and provide enhanced, personalized features.</li>
            <li><strong>Analytical Cookies:</strong> Help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our Services.</li>
            <li><strong>Marketing Cookies:</strong> Used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.</li>
            <li><strong>Security Cookies:</strong> Help us detect malicious activity and protect our platform from security threats, including CSRF protection and session verification.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s3', '3. Types of Cookies We Use')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[#00ff88]/20">
                  <th className="text-left py-2 pr-4 font-semibold">Cookie Name</th>
                  <th className="text-left py-2 pr-4 font-semibold">Purpose</th>
                  <th className="text-left py-2 pr-4 font-semibold">Duration</th>
                  <th className="text-left py-2 font-semibold">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/5">
                  <td className="py-2 pr-4 font-mono text-xs">vtx_session</td>
                  <td className="py-2 pr-4">Maintains your authenticated session</td>
                  <td className="py-2 pr-4">Session</td>
                  <td className="py-2">Essential</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-2 pr-4 font-mono text-xs">vtx_theme</td>
                  <td className="py-2 pr-4">Remembers your dark/light mode preference</td>
                  <td className="py-2 pr-4">1 year</td>
                  <td className="py-2">Preference</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-2 pr-4 font-mono text-xs">vtx_lang</td>
                  <td className="py-2 pr-4">Stores your selected language</td>
                  <td className="py-2 pr-4">1 year</td>
                  <td className="py-2">Preference</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-2 pr-4 font-mono text-xs">_ga, _gid</td>
                  <td className="py-2 pr-4">Google Analytics - visitor statistics</td>
                  <td className="py-2 pr-4">2 years / 24 hours</td>
                  <td className="py-2">Analytical</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-2 pr-4 font-mono text-xs">vtx_csrf</td>
                  <td className="py-2 pr-4">Prevents cross-site request forgery attacks</td>
                  <td className="py-2 pr-4">Session</td>
                  <td className="py-2">Security</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">vtx_consent</td>
                  <td className="py-2 pr-4">Records your cookie consent preferences</td>
                  <td className="py-2 pr-4">1 year</td>
                  <td className="py-2">Essential</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s4', '4. Third-Party Cookies')}</h2>
          <p className="leading-relaxed">
            In addition to our own cookies, we may also use various third-party cookies to report usage statistics, deliver advertisements, and provide other services. These third parties may include analytics providers (such as Google Analytics), advertising networks, and social media platforms. Each third party has its own privacy and cookie policies, and we encourage you to review them.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s5', '5. Managing Your Cookie Preferences')}</h2>
          <p className="leading-relaxed mb-3">
            You have the right to accept or reject cookies. Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline cookies if you prefer. Please note that disabling cookies may affect the functionality and features of our Services.
          </p>
          <p className="leading-relaxed mb-3">
            You can manage your cookie preferences through the following methods:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Browser Settings:</strong> Configure your browser to accept, reject, or notify you when a cookie is being sent. Instructions vary by browser (Chrome, Firefox, Safari, Edge).</li>
            <li><strong>Cookie Banner:</strong> Use our cookie consent banner to select which categories of cookies you wish to enable or disable.</li>
            <li><strong>Privacy Dashboard:</strong> Access your privacy settings in your account dashboard to manage cookie preferences.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s6', '6. Web Beacons and Similar Technologies')}</h2>
          <p className="leading-relaxed">
            In addition to cookies, we may use web beacons (also known as pixel tags, clear GIFs, or single-pixel GIFs), local storage, and other similar technologies. Web beacons are tiny graphics with a unique identifier that are used to track online movements of web users. Unlike cookies, which are stored on your device, web beacons are embedded invisibly on web pages and in emails.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s7', '7. Do Not Track Signals')}</h2>
          <p className="leading-relaxed">
            Some web browsers transmit &quot;Do Not Track&quot; signals to websites. At this time, we do not respond to such signals because no uniform standard for how to interpret them has been established. However, you can manage your cookie preferences as described above.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s8', '8. Changes to This Policy')}</h2>
          <p className="leading-relaxed">
            We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to periodically review this page for the latest information on our cookie practices. The date at the top of this policy indicates when it was last updated.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('cookies.s9', '9. Contact Us')}</h2>
          <p className="leading-relaxed">
            If you have any questions about our use of cookies or this Cookie Policy, please contact us at privacy@zhisu.trade.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}